import { IGenericModelFields } from "../IGenericFields";
import { PresentationActivityType } from "../IPresentationActivityLog";
import { WorkspaceUserRole } from "./IWorkspace";
import { IWorkspacePermissionAccess } from "./IWorkspacePermission";

export enum WorkspaceResourceType {
    /**
     * Regular presentation
     */
    PRESENTATION = "presentation",
    /**
     * Presentation template
     */
    PRESENTATION_TEMPLATE = "presentationTemplate",
    /**
     * Shared slide
     */
    SLIDE = "slide",
    /**
     * Slide template
     */
    SLIDE_TEMPLATE = "slideTemplate",
    /**
     * Shared asset
     */
    ASSET = "asset",
    /**
     * Shared theme or user theme
     */
    THEME = "theme",
}

export interface IWorkspaceResourceBase extends IGenericModelFields {
    readonly workspaceId: string;
    readonly resourceType: WorkspaceResourceType;
    readonly resourceId: string;
    folderIds: string[];
    tags: string[];
    metadata: {
        [key: string]: any;
    };
    permissionsOverrides?: {
        [key in WorkspaceUserRole]?: IWorkspacePermissionAccess;
    }
}

export interface IWorkspaceResourceSlide extends IWorkspaceResourceBase {
    resourceType: WorkspaceResourceType.SLIDE;
    metadata: {
        contentModifiedAt: number;
        isDisabled: boolean;
        name: string;
        description: string;
        usedByUsers: string[];
        usedInPresentations: string[];
        usedInPublicPresentations: string[];
        ratedByUsers: string[];
        createdBy: string;
        modifiedBy: string;
    }
}

export interface IWorkspaceResourceSlideTemplate extends Omit<IWorkspaceResourceSlide, "resourceType"> {
    resourceType: WorkspaceResourceType.SLIDE_TEMPLATE;
}

type LastActivityByType = Partial<Record<PresentationActivityType, {
    userId?: string;
    activity: PresentationActivityType;
    createdAt: number;
}>>;

interface IWorkspaceResourcePresentationMetadata {
    description: string;
    readonly name: string;
    readonly sharedThemeId: string;
    readonly slideCount: number;
    lastActivityByType?: LastActivityByType;
}

export interface IWorkspaceResourcePresentation extends IWorkspaceResourceBase {
    resourceType: WorkspaceResourceType.PRESENTATION;
    metadata: IWorkspaceResourcePresentationMetadata;
}

export interface IWorkspaceResourcePresentationTemplate extends IWorkspaceResourceBase {
    resourceType: WorkspaceResourceType.PRESENTATION_TEMPLATE;
    metadata: IWorkspaceResourcePresentationMetadata & {
        isPublished: boolean;
        isTemplateConfirmed: boolean;
        ratedByUsers: string[];
    }
}

export interface IWorkspaceResourceAsset extends IWorkspaceResourceBase {
    resourceType: WorkspaceResourceType.ASSET;
    metadata: {
        type: string; // type
        createdBy: string; // createdById
        modifiedBy: string; // modifiedById
        name: string; // assetName
        alt: string; // alt
        isPublished: boolean; // !hidden
        actualId: string; // actual asset id
    }
}

export interface IWorkspaceResourceTheme extends IWorkspaceResourceBase {
    resourceType: WorkspaceResourceType.THEME;
    metadata: {
        name: string;
        isDefault: boolean;
        isShared: boolean;
        isPublished: boolean;
    }
}

export type IWorkspaceResource =
    IWorkspaceResourceSlide |
    IWorkspaceResourceSlideTemplate |
    IWorkspaceResourcePresentation |
    IWorkspaceResourcePresentationTemplate |
    IWorkspaceResourceAsset |
    IWorkspaceResourceTheme;

export type IWorkspaceResourceMetadataExtension = {
    metadata: {
        usedByUsersCount: number;
        ratedByUsersCount: number;
        usedInPresentationsCount: number;
        usedInPublicPresentationsCount: number;
    }
}

export type IWorkspaceResourceWithDocument<TDocument = any, TResource = IWorkspaceResource> = {
    resource: TResource & IWorkspaceResourceMetadataExtension;
    permission: IWorkspacePermissionAccess;
    document: TDocument;
};
